import type {
  BotPresence, ExtensionPresence, ExtensionReader, LivePage, ReaderPresence, SyncCommandInput
} from './client';

// Where a command is written: the addressed reader's own inbox, or the shared
// queue every extension drains (refs/reader-ownership.md). Shared by the
// main-thread client and the SharedWorker, which both write commands.
export const commandsPath = (
  ownerId: string,
  input: Pick<SyncCommandInput, 'targetReaderId'>
): string => (input?.targetReaderId
  ? `sync/${ownerId}/readers/${input.targetReaderId}/commands`
  : `sync/${ownerId}/commands`);

// Pure merge of the per-reader presence under `/sync/{ownerId}/readers` into the
// single extension view the app/embed consumes: installed/online is true if ANY
// extension reader reports it, and the open live `pages` are unioned. Non-extension
// readers (bots, API pollers) share the collection but are ignored here — they carry
// no `installed`/`pages`. An untyped node is treated as an extension (backward-lenient
// during the rename). Kept Firebase-free so both the main-thread client (client.ts)
// and the SharedWorker (rtdb.worker.ts) can share it without pulling in firebase-service.
export const mergeExtensionReaders = (
  readers: Record<string, ReaderPresence>
): ExtensionPresence => {
  let installed = false;
  let online = false;
  const pages: Record<string, LivePage> = {};
  const list: ExtensionReader[] = [];
  Object.entries(readers || {}).forEach(([readerId, reader]) => {
    if ((reader?.type ?? 'extension') !== 'extension') return;
    if (reader?.installed) installed = true;
    if (reader?.online) online = true;
    Object.assign(pages, reader?.pages || {});
    list.push({
      readerId,
      online: !!reader?.online,
      version: reader?.version ?? null,
      userId: reader?.userId ?? null,
      userEmail: reader?.userEmail ?? null,
      label: reader?.label ?? null,
      capabilities: Array.isArray(reader?.capabilities) ? reader.capabilities : [],
      pages: reader?.pages || {},
      lastSeen: reader?.lastSeen
    });
  });
  return {
    installed, online, pages, readers: list
  };
};

// The other half of the same `/sync/{ownerId}/readers` snapshot: meeting bots,
// keyed by the livestream they were dispatched for. Unlike extension readers
// these are never merged — one livestream has exactly one bot, and its `status`
// is the whole point.
//
// Platform-agnostic: a Zoom SDK bot, an Attendee.dev bot behind Meet or Teams,
// and anything added later all land here, because they all publish the same
// `type:'bot'` node with the same status vocabulary.
//
// Read `status`, never mere existence. A browser-hosted bot's node removes itself
// when its socket drops (onDisconnect), but a webhook-driven bot holds no socket:
// its node persists with whatever it last reported until the disconnect flow
// retires it, so a present reader is not proof of a live bot.
export const mergeBotReaders = (
  readers: Record<string, ReaderPresence>
): Record<string, BotPresence> => {
  const bots: Record<string, BotPresence> = {};
  Object.values(readers || {}).forEach((reader) => {
    if (reader?.type !== 'bot') return;
    if (!reader?.livestreamId || reader?.online === false) return;
    bots[String(reader.livestreamId)] = {
      livestreamId: String(reader.livestreamId),
      status: reader.status,
      isInMeeting: !!reader.isInMeeting,
      platform: reader.platform,
      runtime: reader.runtime,
      meetingId: reader.meetingId,
      detail: reader.detail,
      lastSeen: reader.lastSeen
    };
  });
  return bots;
};
